// @eidollon/contracts/search
//
// SEARCH CONTRACT — request/response shapes for /v1/search/* and /v1/ai/*.
//
// `services/api` validates against these on the wire. `apps/terminal`
// consumes via TanStack Query. `services/inference` defines its own internal
// contracts for /v1/inference/run; they're not exposed to the browser.
//
// Per CLAUDE.md Iron Rule 11, every new entity browser uses these.

import { z } from 'zod';
import { EntityKind, OpportunityRow } from './api.js';
import { FilterAst, FilterState, SortSpec } from './filters.js';
import { SupportedTargetLang } from './translation.js';

/* ──────────────────────────────────────────────────────────────────────────
 *  Search request / response — POST /v1/search/{entity}
 * ────────────────────────────────────────────────────────────────────────── */

export const SearchRequest = z
  .object({
    q: z.string().min(1).max(200).optional(), // free-text; FTS today, hybrid tomorrow
    ast: FilterAst.optional(), // supersedes `state` when both present
    state: FilterState.optional(), // flat-map convenience for rail/chip modes
    sort: SortSpec.optional(),
    cursor: z.string().max(512).optional(), // opaque, server-issued
    // Absolute page offset (rows). Mutually compatible with keyset cursors:
    // when both are supplied `cursor` wins (keyset is stable across writes;
    // offset is the URL-friendly fallback used by page-N navigation).
    // Capped at 1_000_000 — beyond that the user should refine filters, not
    // walk deeper.
    offset: z.number().int().min(0).max(1_000_000).optional(),
    limit: z.number().int().min(1).max(200).default(50),
    personalize: z.boolean().default(false), // adds tenant_interest_vector boost (Phase B)
    // Optional render-language hint. When provided AND != 'en', the search
    // route LEFT JOINs `translations` to swap `title` (and `description` where
    // present) with the precomputed localized text. Omitted or 'en' = no
    // join, no perf impact, identical behavior to pre-translation builds.
    // Per Iron Rule 12: zero LLM in the hot path; falls back to the source
    // column when no precomputed row exists.
    target_lang: SupportedTargetLang.optional(),
  })
  .strict();
export type SearchRequest = z.infer<typeof SearchRequest>;

export const SearchResponseMeta = z
  .object({
    total: z.number().int().nullable(), // null when COUNT(*) is too expensive
    cursor: z.string().nullable(),
    latencyMs: z.number().nonnegative(),
    fromCache: z.boolean(),
    backend: z.enum(['postgres-fts', 'opensearch-hybrid']),
  })
  .strict();
export type SearchResponseMeta = z.infer<typeof SearchResponseMeta>;

export const OpportunitySearchResponse = z
  .object({
    rows: z.array(OpportunityRow),
    meta: SearchResponseMeta,
  })
  .strict();
export type OpportunitySearchResponse = z.infer<typeof OpportunitySearchResponse>;

/* ──────────────────────────────────────────────────────────────────────────
 *  Aggregations — POST /v1/search/{entity}/aggregations
 *
 *  One round-trip computes facet counts for *all* fields except the field
 *  being filtered (so a multi-select stays "live" against everything else).
 * ────────────────────────────────────────────────────────────────────────── */

export const AggregationsRequest = z
  .object({
    q: z.string().min(1).max(200).optional(),
    ast: FilterAst.optional(),
    state: FilterState.optional(),
    // Which facets to compute. Server caps cardinality per field at 50 + "_other".
    fields: z.array(z.string().min(1).max(64)).min(1).max(32),
  })
  .strict();
export type AggregationsRequest = z.infer<typeof AggregationsRequest>;

export const AggregationBucket = z
  .object({
    value: z.string(),
    count: z.number().int().nonnegative(),
  })
  .strict();
export type AggregationBucket = z.infer<typeof AggregationBucket>;

export const AggregationsResponse = z
  .object({
    fields: z.record(
      z.string(),
      z.object({
        buckets: z.array(AggregationBucket).max(51), // 50 + "_other"
        other: z.number().int().nonnegative().default(0),
      }),
    ),
    meta: z
      .object({
        latencyMs: z.number().nonnegative(),
        fromCache: z.boolean(),
      })
      .strict(),
  })
  .strict();
export type AggregationsResponse = z.infer<typeof AggregationsResponse>;

/* ──────────────────────────────────────────────────────────────────────────
 *  AI suggestions — POST /v1/ai/suggest
 *
 *  Hot path. Serves from precomputed `tenant_interest_vectors` × pgvector.
 *  ZERO LLM calls in the user request path. Per-card "why" strings are
 *  cached 24h on (tenant_id, opp_id, tenant_vec_version).
 * ────────────────────────────────────────────────────────────────────────── */

export const SuggestionKind = z.enum([
  'high_fit', // top-K cosine
  'incumbent_at_risk', // tenant's expiring contracts with no recompete posted
  'anomaly', // agency × NAICS posting bursts
  'shaping_window', // RFI / sources-sought open in tenant's focus area
]);
export type SuggestionKind = z.infer<typeof SuggestionKind>;

export const Suggestion = z
  .object({
    id: z.string(), // stable across refresh
    kind: SuggestionKind,
    entity_kind: EntityKind,
    entity_id: z.string(), // opp / award / contract id
    label: z.string().max(160), // one-line claim
    why: z.array(z.string().max(160)).max(5), // bullets, optional drill-down
    confidence: z.number().min(0).max(1),
  })
  .strict();
export type Suggestion = z.infer<typeof Suggestion>;

export const SuggestRequest = z
  .object({
    context: z.enum(['horizon', 'terminal', 'brief', 'ops']),
    q: z.string().max(200).optional(),
    cursor: z.string().max(64).optional(),
    // Opt-out per category (user prefs). Default = include all.
    exclude: z.array(SuggestionKind).optional(),
  })
  .strict();
export type SuggestRequest = z.infer<typeof SuggestRequest>;

export const SuggestResponse = z
  .object({
    suggestions: z.array(Suggestion).max(20),
    meta: z
      .object({
        coldStart: z.boolean(), // tenant has <25 signals
        vectorAge: z.number().int().nonnegative(), // hours since last refresh
        fromCache: z.boolean(),
        latencyMs: z.number().nonnegative(),
      })
      .strict(),
  })
  .strict();
export type SuggestResponse = z.infer<typeof SuggestResponse>;

/* ──────────────────────────────────────────────────────────────────────────
 *  Natural-language → FilterAst — POST /v1/ai/nl-rewrite (Phase C)
 *
 *  Wire the request shape now (it's the boundary the inference gateway will
 *  produce against). Response shape lands with the route in Phase C — adding
 *  it speculatively before there's a producer or consumer is slop.
 * ────────────────────────────────────────────────────────────────────────── */

export const NlRewriteRequest = z
  .object({
    text: z.string().min(1).max(400),
    entity: z.string().min(1).max(32), // 'OPPORTUNITY' | 'AWARD' | …
  })
  .strict();
export type NlRewriteRequest = z.infer<typeof NlRewriteRequest>;

/* ──────────────────────────────────────────────────────────────────────────
 *  Feedback — POST /v1/search/feedback
 *
 *  Click / hide / pin / ignore. Feeds nightly tenant interest vector.
 * ────────────────────────────────────────────────────────────────────────── */

export const FeedbackSignal = z.enum(['click', 'hide', 'pin', 'ignore']);
export type FeedbackSignal = z.infer<typeof FeedbackSignal>;

export const FeedbackRequest = z
  .object({
    query_id: z.string().uuid(),
    opportunity_id: z.string().uuid(),
    signal: FeedbackSignal,
    // Optional client-side weight nudge (default 1 server-side). Most callers
    // omit it; it exists for the rare "this was an accidental click" demotion.
    weight: z.number().min(0).max(10).optional(),
  })
  .strict();
export type FeedbackRequest = z.infer<typeof FeedbackRequest>;
